import {
  PaginatedAPIRequest,
  PaginationOptions,
  PaginationAPIActionGenerator,
  PaginatedAPIData,
  PaginationSagaOptions,
  PaginationSelector,
  emptyPaginatedAPIData,
  IndirectPaginatedAPIData,
  emptyIndirectPaginatedAPIData,
  IndirectPaginationOptions,
  IndirectPaginationAPIActionGenerator,
} from './common';
import {
  APIAction,
  createAPIActionGenerator,
  IndirectAPIData,
  emptyIndirectAPIData,
} from '../apidata';
import { paginationStore, indirectPaginationStore } from './store';
import { paginationCustomSaga, paginationSaga } from './saga';
import {
  MapIndirectOptions,
  MapIndirectAction,
  paginationMapIndirect,
} from './map';

/**
 * Function encapsulting common redux action types to be used for generating
 * state and saga logic
 */
export function createPaginationAPIActionGenerator<
  /** Request type */
  R extends PaginatedAPIRequest<T, E>,
  /** Native page type */
  T,
  /** Entry type */
  P,
  /** Error type */
  E = unknown
>(
  opts: PaginationOptions<R, T, P, E>
): PaginationAPIActionGenerator<R, T, P, E> {
  const generator = createAPIActionGenerator<R, T, E>(opts);

  const store =
    () =>
    (
      state: PaginatedAPIData<P, E> = emptyPaginatedAPIData,
      action: APIAction<R, T, E>
    ) =>
      paginationStore(opts, generator, state, action);

  const customSaga = (sagaOpts: PaginationSagaOptions<R, T, E>) =>
    paginationCustomSaga(opts, generator, sagaOpts);

  const saga = (
    getInitialURL: (request: R) => string,
    selector: PaginationSelector<R, P, E>
  ) => paginationSaga(opts, generator, getInitialURL, selector);

  const mapIndirect =
    (mapOpts: MapIndirectOptions<P>) =>
    (
      state: IndirectAPIData<P> = emptyIndirectAPIData,
      action: MapIndirectAction<T, P>
    ) =>
      paginationMapIndirect(opts, generator, mapOpts, state, action);

  return {
    ...generator,
    store,
    customSaga,
    saga,
    mapIndirect,
  };
}

/**
 * Function encapsulting common redux action types to be used for generating
 * state and saga logic for indirect pagination data
 */
export function createIndirectPaginationAPIActionGenerator<
  /** Request type */
  R extends PaginatedAPIRequest<T, E>,
  /** Native page type */
  T,
  /** Entry type */
  P,
  /** Error type */
  E = unknown
>(
  opts: IndirectPaginationOptions<R, T, P, E>
): IndirectPaginationAPIActionGenerator<R, T, P, E> {
  const generator = createPaginationAPIActionGenerator<R, T, P, E>(opts);

  const store =
    () =>
    (
      state: IndirectPaginatedAPIData<P, E> = emptyIndirectPaginatedAPIData,
      action: APIAction<R, T, E>
    ) =>
      indirectPaginationStore(opts, generator, state, action);

  return {
    ...generator,
    store,
  };
}
